import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import PreviewIcon from "@mui/icons-material/Preview";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { searchInvites } from "../../data/omniDataAPI";
import { createNewCommunicationRequest } from "../../data/omniDataModel";
import * as AdminSessionActions from "../../state/redux/adminSession";
import { createOmniPaginationObject } from "../../utils/helpers";
import OmniDatePicker from "../ui/omniDatePicker";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniEditButton from "../ui/omniEditButton";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniTextField from "../ui/omniTextField";

const CommunicationRequestBulkDialog = props => {
  const {
    open,
    dialogTitle = "Bulk Communication Requests",
    cancelMessage = "Cancel",
    okMessage = "OK",
    maxWidth = "sm",
    fullWidth = true,
    emailTemplates = [],
    onClose,
  } = props;

  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();

  const [campaign, setCampaign] = useState("");

  const [selectedTemplateID, setSelectedTemplateID] = useState(null);
  const [campaignValid, setCampaignValid] = useState(false);

  const [localPaginiation, setLocalPagination] = useState(null);

  const createLocalPaginiation = campaignValue => {
    let newPage = createOmniPaginationObject(null, 10);

    newPage.pageIndex = 1;
    newPage.pageSize = 5;

    let newQuery = {};
    newQuery["omniData.target"] = {
      "#regex": `${campaignValue}`,
      "#options": "i",
    };
    newQuery["omniData.inviteType"] = constants.inviteType.single;

    //move this out?
    newQuery["omniData.invitationStatus"] = "created";
    newPage.query = newQuery;
    newPage.sort = {};
    return newPage;
  };

  useEffect(() => {
    if (localPaginiation) {
      searchInvites(localPaginiation)
        .then(results => {
          if (results.items.length > 0) {
            let target = results.items[0].omniData.target;
            let lastIndex = target.lastIndexOf("_");
            target = target.substr(++lastIndex);
            target = target.split("+");

            let currentCount = parseInt(target[0]);
            let pageSize = parseInt(target[1]);
            let pageCount = Math.floor(currentCount / pageSize);

            let newRequest = createNewCommunicationRequest();

            newRequest.omniData["organizationId"] = props.organizationODM._id;
            newRequest.omniData.communicationTemplateId = selectedTemplateID;
            newRequest.omniData.initialCount = results.pageInfo.itemCount;
            newRequest.omniData.description = localPaginiation.query["omniData.target"]["#regex"];

            let newDate = new Date(sendDate);
            newDate.setDate(newDate.getDate() + pageCount);
            newRequest.omniData.sendDate = newDate.toISOString();

            newRequest.omniData.destinations = {
              to: {
                partnerIds: [],
                partnerQuery: null, // # Query object for the /communicationRequest/search endpoint
                inviteIds: [],
                inviteQuery: { query: localPaginiation.query }, // # Query object from /invites/search
              },
            };

            props.updateCommunicationRequestRequest(newRequest);

            currentCount += pageSize;
            let newPage = createLocalPaginiation(
              `${campaign}_${currentCount.toString().padStart(4, "0")}`,
            );

            setLocalPagination(newPage);
          } else {
            enqueueSnackbar("Process Complete!", {
              variant: "success",
            });
            setLocalPagination(null);
            onClose();
          }
        })
        .catch(err => {
          enqueueSnackbar(`Error: ${err}`, {
            variant: "error",
          });
          setLocalPagination(null);
          onClose();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPaginiation]);

  const onAcceptHandler = () => {
    //do a local search for the campaign with 0000
    enqueueSnackbar("Creating, please do not refresh your browser.", {
      variant: "info",
    });

    let myCampaign = campaign.trim();
    let newPage = createLocalPaginiation(`${myCampaign}_0000`);

    setLocalPagination(newPage);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const [sendDate, setSendDate] = useState(new Date());

  const [filteredTemplates, setFilteredTemplates] = useState([]);

  useEffect(() => {
    if (filteredTemplates.length > 0) {
      setSelectedTemplateID(filteredTemplates[0]._id);
    } else {
      setSelectedTemplateID(null);
    }
  }, [filteredTemplates]);

  useEffect(() => {
    if (Object.keys(campaign).length) {
      setCampaignValid(true);
    } else {
      setCampaignValid(false);
    }
  }, [campaign, campaignValid]);

  useEffect(() => {
    setFilteredTemplates(
      emailTemplates.filter(item => item.omniData.communicationTarget === "invitees"),
    );
  }, [emailTemplates]);

  useEffect(() => {
    campaign ? setCampaignValid(true) : setCampaignValid(false);
  }, [campaign]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogContent>
        {/* INFORMATION TAB */}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}
            >
              {
                "Enter the exact campaign value you used when creating invites via the import feature."
              }
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8}>
            <OmniTextField
              label="Campaign"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              id={"campaign"}
              value={campaign}
              error={!campaignValid}
              onChange={event => {
                setCampaign(event.target.value);
              }}
              placeholder={"Exact Campaign Value"}
              required
            />
          </Grid>
          <Grid item xs={4} sm={4}>
            <OmniDatePicker
              label="Send First Request"
              value={sendDate}
              name={uuid()} //prevents AutoComplete Suggestions
              variant="inline"
              inputVariant="outlined"
              onAccept={null}
              onChange={date => setSendDate(date)}
              format="MM/dd/yyyy"
              fullWidth
            />
          </Grid>
          <Grid item xs={10} sm={11}>
            {selectedTemplateID && (
              <OmniTextField
                label="Communication Template"
                // name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                // id={"communicationTarget"}
                value={selectedTemplateID}
                select
                onChange={event => {
                  setSelectedTemplateID(event.target.value);
                }}
              >
                {filteredTemplates.map(templateODM => {
                  return (
                    <OmniMenuItem key={templateODM._id} value={templateODM._id}>
                      {`${templateODM.omniData.name} - ${templateODM.omniData.description}`}
                    </OmniMenuItem>
                  );
                })}
              </OmniTextField>
            )}
            {!selectedTemplateID && <Typography>No Templates for Invitees</Typography>}
          </Grid>
          <Grid item xs={2} sm={1}>
            <Box sx={{ position: "relative" }}>
              <OmniEditButton
                sx={{ top: 11 }}
                title={"Copy GUID to Clipboard"}
                disabled={selectedTemplateID == null}
                onClick={e => {
                  e.stopPropagation();

                  let template = filteredTemplates.find(temp => temp._id === selectedTemplateID);
                  if (template) {
                    window.open(template.omniData.templateUrl, "_blank", "noreferrer");
                  }
                }}
                icon={<PreviewIcon size="large" />}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}></Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={!campaignValid}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  communicationTemplateODMs: state.adminSession.communicationTemplateODMs,
  communicationRequestODMs: state.adminSession.communicationRequestODMs,
  organizationODM: state.userSession.organizationODM,
});

const mapDispatchToProps = {
  loadCommunicationRequestsRequest: AdminSessionActions.loadCommunicationRequestsRequest,
  updateCommunicationRequestRequest: AdminSessionActions.updateCommunicationRequestRequest,
  deleteCommunicationRequestRequest: AdminSessionActions.deleteCommunicationRequestRequest,

  loadCommInvitesRequest: AdminSessionActions.loadCommInvitesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommunicationRequestBulkDialog);
